import { useMemo } from 'react';
import { sortBy } from 'lodash';
import SidePanel, { PanelMainGroup, PanelSection } from '../SidePanel';
import {
  CropVarietyDropdown,
  LabeledData,
} from '../right-menu/menu-components';
import { asDynamicDecimal, asQuantityWithUnit } from '../../utils/formatters';
import { LONG_AREA_UNITS } from '../../constants/units';
import { useMultiProjectDashboardContext } from '../../contexts/MultiProjectDashboardContext';
import DashboardExportButtons from './DashboardExportButtons';
import { useToggleState } from '../../utils/hooks';
import { captureEvent } from '../../utils/analytics';
import CropData from '../right-menu/CropData';

const DashboardRightMenu = () => {
  const {
    cropColorOptions,
    getColorForCropName,
    dashboards: { dashboardFarms, setCropColor, dashboardLoading },
  } = useMultiProjectDashboardContext();

  const [varietiesIsOpen, toggleVarietiesOpen] = useToggleState();

  // get values that result in a single number across all farms
  const { sectionCount, acreage, treeCount } = useMemo(() => {
    let tempSectionCount = 0;
    let tempAcreage = 0;
    let tempTreeCount = 0;
    dashboardFarms.forEach((farm) => {
      tempSectionCount += farm.sectionCount;
      tempAcreage += farm.ecologicalSummary.acreage;
      tempTreeCount += farm.ecologicalSummary.treeCount;
    });
    return {
      sectionCount: tempSectionCount,
      acreage: tempAcreage,
      treeCount: tempTreeCount,
    };
  }, [dashboardFarms]);

  // get values that result in a number per crop by name
  const cropValues = useMemo(() => {
    const byCrop = {};
    dashboardFarms.forEach((farm) => {
      farm.cropAggregates.forEach(
        ({ crop = {}, ecologicalSummary = {}, cropVarietyAggregates = [] }) => {
          if (!byCrop[crop.name]) {
            byCrop[crop.name] = {
              cropId: crop.name,
              crop: {
                name: crop.name,
                color: getColorForCropName(crop.name),
                cropVarieties: [],
              },
              treeCount: 0,
              acreage: 0,
              cropSectionVarieties: {},
            };
          }
          byCrop[crop.name].treeCount += ecologicalSummary.treeCount;
          byCrop[crop.name].acreage += ecologicalSummary.acreage;
          cropVarietyAggregates.forEach(
            ({ cropVariety, treeCount: cropVarietyTreeCount }) => {
              if (!byCrop[crop.name].cropSectionVarieties[cropVariety.name]) {
                byCrop[crop.name].cropSectionVarieties[cropVariety.name] = {
                  cropVarietyId: cropVariety.id,
                  treeCount: 0,
                };
                byCrop[crop.name].crop.cropVarieties.push(cropVariety);
              }
              byCrop[crop.name].cropSectionVarieties[
                cropVariety.name
              ].treeCount += cropVarietyTreeCount;
            },
          );
        },
      );
    });

    return sortBy(Object.values(byCrop), (cropAgg) => cropAgg.crop.name).map(
      (cropAgg) => ({
        ...cropAgg,
        cropSectionVarieties: Object.values(cropAgg.cropSectionVarieties),
      }),
    );
  }, [dashboardFarms, getColorForCropName]);

  const hasCropVarieties = useMemo(
    () => cropValues.some((crop) => !!crop.cropSectionVarieties.length),
    [cropValues],
  );

  return (
    <SidePanel right>
      {dashboardLoading ? (
        <div className="m-3">Loading...</div>
      ) : (
        <PanelMainGroup>
          <PanelSection>
            <h4>Farms</h4>
            <LabeledData
              label="qty"
              formatter={asQuantityWithUnit('farms', 'farm')}
            >
              {dashboardFarms?.length || 0}
            </LabeledData>
          </PanelSection>
          <PanelSection>
            <h4>Sections</h4>
            <LabeledData
              copyable
              label="size"
              formatter={asDynamicDecimal}
              unitSpec={LONG_AREA_UNITS}
            >
              {acreage}
            </LabeledData>
            <LabeledData
              copyable
              label="qty"
              formatter={asQuantityWithUnit('sections', 'section')}
            >
              {sectionCount}
            </LabeledData>
            <LabeledData
              copyable
              label="stems"
              formatter={asQuantityWithUnit('stems', 'stem')}
            >
              {treeCount}
            </LabeledData>
          </PanelSection>
          {dashboardFarms.length > 0 && (
            <>
              <PanelSection>
                <h4>Export</h4>
                <DashboardExportButtons />
              </PanelSection>
              <PanelSection className="pb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h4 className="m-0">Crops</h4>
                  {hasCropVarieties && (
                    <CropVarietyDropdown
                      isOpen={varietiesIsOpen}
                      onClick={() => {
                        captureEvent('dashboard-toggle-varieties');
                        toggleVarietiesOpen();
                      }}
                      plural
                    />
                  )}
                </div>
                {cropValues.map(
                  ({
                    cropId,
                    crop,
                    treeCount: cropTreeCount,
                    acreage: cropAcreage,
                    cropSectionVarieties,
                  }) => (
                    <CropData
                      className="mb-2"
                      key={cropId}
                      crop={crop}
                      qty={cropTreeCount}
                      acreage={cropAcreage}
                      showVarieties={varietiesIsOpen}
                      varieties={cropSectionVarieties}
                      colors={cropColorOptions}
                      onSelectColor={(color) =>
                        setCropColor(crop.name, color.id)
                      }
                    />
                  ),
                )}
              </PanelSection>
            </>
          )}
        </PanelMainGroup>
      )}
    </SidePanel>
  );
};

export default DashboardRightMenu;
